import React, { useState } from 'react';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import '../Workflow.scss'
import classnames from 'classnames'
import WorkflowTask from '../../../../../components/library/workflow-tasks/WorkflowTask'
import { useTaskStatus } from '../../hooks/useTaskStatusHook';
import { WorkflowTasks } from '../../../services/WorkflowTasks';
import { TaskStatus } from '../../../services/TaskStatus';
import WorkflowTaskDescriptionWithHelp from '../../../../../components/library/workflow-tasks/WorkflowTaskDescriptionWithHelp';
import VerticalNameChildren from '../../../../../components/library/layout/VerticalNameChildren';
import { useInterviewInfo } from '../../hooks/useInterviewInfoHook';
import LoadingTextValue from '../../../../../components/library/layout/LoadingTextValue';
import HrbTranslate from '../../../../../components/language/HrbTranslate';
import DateTime from '../../../../../components/library/common/DateTime';
import EmdashText from '../../../../../components/library/common/EmdashText';
import { shallowEqual } from 'react-redux';
import BlockSignalLink from '../../../../../components/library/common/BlockSignalLink';

const IdentityConfirmationTask = (props) => {
    const [returnType, , , primaryName, spouseName] = useInterviewInfo(props.ticket);

    let tasks = returnType === 'coupled' ?
        [WorkflowTasks.CONFIRM_ID, WorkflowTasks.CONFIRM_ID_SPOUSE] :
        [WorkflowTasks.CONFIRM_ID]

    const [baseStatus, taskCompletions] = useTaskStatus(props.ticket, tasks);

    // for overriding status on panel
    const [status, setStatus] = useState(TaskStatus.NOT_STARTED);

    // track individual statii for each filer
    const [primaryStatus, setPrimaryStatus] = useState('')
    const [spouseStatus, setSpouseStatus] = useState('')
    // task completion data for each filer
    const [primaryCompletion, setPrimaryCompletion] = useState({})
    const [spouseCompletion, setSpouseCompletion] = useState({})

    useEffect(() => {
        let tcs = taskCompletions || []

        let primary = tcs.find(x => x.status !== TaskStatus.INVALID && x.workflowTaskId === WorkflowTasks.CONFIRM_ID)
        let primaryData = primary && primary.completionData ? JSON.parse(primary.completionData) : {}
        if (primary) setPrimaryStatus(primary.status || '')
        if (!shallowEqual(primaryData, primaryCompletion)) { setPrimaryCompletion(primaryData) }

        let spouse = tcs.find(x => x.status !== TaskStatus.INVALID && x.workflowTaskId === WorkflowTasks.CONFIRM_ID_SPOUSE)
        let spouseData = spouse && spouse.completionData ? JSON.parse(spouse.completionData) : {}
        if (spouse) setSpouseStatus(spouse.status || '')
        if (!shallowEqual(spouseData, spouseCompletion)) { setSpouseCompletion(spouseData) }

    }, [taskCompletions])

    useEffect(() => {
        if ((primaryStatus === TaskStatus.COMPLETE && !primaryCompletion.Timestamp) ||
            (spouseStatus === TaskStatus.COMPLETE && !spouseCompletion.Timestamp)) {
            setStatus(TaskStatus.ERROR)
        } else {
            setStatus(baseStatus)
        }
    }, [baseStatus, primaryStatus, spouseStatus])

    const renderCompletionStatus = (compData, failure) => {
        if (failure) {
            return <p className='p1 medium-weight'><HrbTranslate text="ConfirmIdTask.FailedOnline" /></p>
        } else {
            switch (compData.Method) {
                case 'Basic':
                    return <p className='p1 medium-weight'><HrbTranslate text="ConfirmIdTask.VerifiedOnline" /></p>
                case 'Direct':
                    return <p className='p1 medium-weight'><HrbTranslate text="ConfirmIdTask.VerifiedByPhone" /></p>
                case 'Selfie':
                    return <p className='p1 medium-weight'><HrbTranslate text="ConfirmIdTask.StepOneFailure" /></p>
                default:
                    return <p className='p1 medium-weight'><HrbTranslate text="Tickets.Tasks.TaskStatus.NotStarted" /></p>
            }
        }
    }

    const renderTimestamp = (compData, complete) => {
        if (complete) {
            return compData.Timestamp ?
                <p className='p1 medium-weight'><DateTime dateAndTime value={compData.Timestamp} /></p> :
                <HrbTranslate text='ConfirmIdTask.ErrorTimestamp' params={{ BlockSignal: <BlockSignalLink /> }} className='p2 error' />
        } else {
            return <p className='p1 medium-weight'><EmdashText /></p>
        }
    }

    const renderErrorStatus = () => {
        if (status === TaskStatus.FAILED) {
            let primaryFailed = primaryStatus === TaskStatus.FAILED
            let spouseFailed = spouseCompletion.Status === TaskStatus.FAILED

            let errorKey = spouseFailed && primaryFailed ?
                'ConfirmIdTask.ErrorMultipleVerificationFailure' :
                'ConfirmIdTask.ErrorSingleVerificationFailure'
            let params = spouseFailed && primaryFailed ?
                { PrimaryName: primaryName, SpouseName: spouseName } :
                { Name: primaryFailed ? primaryName : spouseName }

            return <div className='columns'>
                <div className='column is-8 is-offset-1'>
                    <HrbTranslate text={errorKey} params={params} className='p2 error' />
                </div>
            </div>
        }
    }

    return (
        <WorkflowTask status={status} taskId={WorkflowTasks.CONFIRM_ID}>
            <div className='columns'>
                <WorkflowTaskDescriptionWithHelp
                    helpKey='0ProSupport/VDOPro/IDConfirmation.htm'
                    descriptions={['ConfirmIdTask.Description']} />
            </div>
            <div className='columns'>
                <div className={classnames('column is-offset-1', { 'is-4': returnType === 'coupled', 'is-8': returnType !== 'coupled' })}>
                    <h4><LoadingTextValue loading={!props.ticket} value={primaryName} /></h4>
                </div>
                {returnType === 'coupled' &&
                    <div className='column is-4'>
                        <h4><LoadingTextValue loading={!props.ticket} value={spouseName} /></h4>
                    </div>
                }
                <div className='column is-3' />
            </div>
            <div className='columns'>
                <div className={classnames('column is-offset-1', { 'is-4': returnType === 'coupled', 'is-8': returnType !== 'coupled' })}>
                    <VerticalNameChildren
                        textKey="ConfirmIdTask.VerificationStatus"
                        loading={!props.ticket}>
                        {renderCompletionStatus(primaryCompletion, primaryStatus === TaskStatus.FAILED)}
                    </VerticalNameChildren>
                </div>
                {returnType === 'coupled' &&
                    <div className='column is-4'>
                        <VerticalNameChildren
                            textKey="ConfirmIdTask.VerificationStatus"
                            loading={!props.ticket}>
                        {renderCompletionStatus(spouseCompletion, spouseStatus === TaskStatus.FAILED)}
                        </VerticalNameChildren>
                    </div>
                }
                <div className='column is-3' />
            </div>
            <div className='columns'>
                <div className='column is-1' />
                <div className={classnames('column', { 'is-4': returnType === 'coupled', 'is-8': returnType !== 'coupled' })}>
                    <VerticalNameChildren
                        textKey="ConfirmIdTask.LastVerifiedOn"
                        loading={!props.ticket}>
                        {renderTimestamp(primaryCompletion, primaryStatus === TaskStatus.COMPLETE)}
                    </VerticalNameChildren>
                </div>
                {returnType === 'coupled' &&
                    <div className='column is-4'>
                        <VerticalNameChildren
                            textKey="ConfirmIdTask.LastVerifiedOn"
                            loading={!props.ticket}>
                        {renderTimestamp(spouseCompletion, spouseStatus === TaskStatus.COMPLETE)}
                        </VerticalNameChildren>
                    </div>
                }
                <div className='column is-3' />
            </div>
            {renderErrorStatus()}
        </WorkflowTask>
    );
}

IdentityConfirmationTask.whyDidYouRender = false;

IdentityConfirmationTask.propTypes = {
    ticket: PropTypes.object
}

export default IdentityConfirmationTask